import {
  Badge,
  Box,
  Group,
  Stack,
  Text,
} from "@mantine/core"
import {
  CheckCircleIcon,
  TruckIcon,
  WarningCircleIcon,
  WarningIcon,
} from "@phosphor-icons/react"
import { useTranslation } from "react-i18next"
import { HelpIcon } from "@dehub/theme"
import classes from "./wizard.module.css"
import type { DnRow } from "./types"

interface Props {
  stops          : DnRow[]
  detectedChannel: "LOCAL_DELIVERY" | "MIXED" | null
  plannedDate    : string | null
  totalDistanceKm: number
}

interface StatRowProps {
  label : string
  value : React.ReactNode
}

function StatRow({ label, value }: StatRowProps) {
  return (
    <Group justify="space-between" py={6} style={{ borderBottom: "1px solid var(--mantine-color-gray-1)" }}>
      <Text size="sm" c="dimmed">{label}</Text>
      <Box>{typeof value === "string" || typeof value === "number" ? <Text size="sm" fw={700}>{value}</Text> : value}</Box>
    </Group>
  )
}

interface ValRowProps {
  status : "pass" | "warn" | "fail"
  bold   : string
  detail?: string
}

function ValRow({ status, bold, detail }: ValRowProps) {
  const color = status === "pass" ? "green" : status === "warn" ? "orange" : "red"
  const Icon  = status === "pass" ? CheckCircleIcon : status === "warn" ? WarningIcon : WarningCircleIcon
  return (
    <Group gap={6} py={4} wrap="nowrap" align="flex-start">
      <Icon size={14} weight="fill" color={`var(--mantine-color-${color}-6)`} />
      <Text size="sm" c={color}>
        <Text span fw={700}>{bold}</Text>
        {detail && <Text span c="dimmed"> ({detail})</Text>}
      </Text>
    </Group>
  )
}

// Rough average city speed used for ETA hint. Not a routing engine — just
// a sanity number to show on the right panel until we wire a map service.
const CITY_AVG_KMH = 20

export function StatsPanel({ stops, detectedChannel, plannedDate, totalDistanceKm }: Props) {
  const { t } = useTranslation(["delivery"])
  const orderCount  = stops.length
  const totalItems  = stops.reduce((sum, dn) => sum + (dn.totalItemCount ?? 0), 0)
  const totalSize   = stops.reduce((sum, dn) => {
    const raw = dn.classification?.totalSizeValue
    const n   = raw == null ? 0 : Number(raw)
    return sum + (Number.isFinite(n) ? n : 0)
  }, 0)
  const vipCount    = stops.filter((dn) => dn.sla?.priority === "VIP").length
  const urgentCount = stops.filter((dn) => dn.sla?.priority === "URGENT").length
  const slaBreached = stops.filter((dn) => dn.sla?.slaBreached).length
  const unclassified = stops.filter((dn) => !dn.classification?.deliveryChannel).length
  const dates       = new Set(stops.map((dn) => dn.deliveryDate?.slice(0, 10)).filter(Boolean))

  return (
    <Box component="section" className={classes.card}>
      <Box className={classes.head}>
        <Text className={classes.title}>{t("delivery:route.wizardUi.stats.title")}<HelpIcon helpKey="order.delivery-routes-new.section.stats" variant="section" /></Text>
      </Box>

      {/* Stats list */}
      <Box px={18} py="xs">
        <Stack gap={0}>
          <StatRow label={t("delivery:route.wizardUi.stats.orders")} value={orderCount} />
          <StatRow label={t("delivery:route.wizardUi.stats.items")} value={totalItems > 0 ? t("delivery:route.wizardUi.stats.itemsValue", { count: totalItems }) : "-"} />
          <StatRow
            label={t("delivery:route.wizardUi.stats.size")}
            value={
              totalSize > 0 ? (
                <Badge size="sm" variant="default" radius="sm" ff="monospace">
                  {totalSize.toFixed(0)}S
                </Badge>
              ) : <Text size="sm" c="dimmed">-</Text>
            }
          />
          <StatRow
            label={t("delivery:route.wizardUi.stats.vip")}
            value={
              vipCount + urgentCount > 0
                ? t("delivery:route.wizardUi.stats.vipValue", { vip: vipCount, urgent: urgentCount })
                : <Text size="sm" c="dimmed">-</Text>
            }
          />
          <StatRow
            label={t("delivery:route.wizardUi.stats.distance")}
            value={totalDistanceKm > 0 ? `${totalDistanceKm.toFixed(1)} ${t("delivery:route.unit.km")}` : <Text size="sm" c="dimmed">-</Text>}
          />
          <StatRow
            label={t("delivery:route.wizardUi.stats.duration")}
            value={
              totalDistanceKm > 0
                ? `${Math.round((totalDistanceKm / CITY_AVG_KMH) * 60)} ${t("delivery:route.unit.min")}`
                : <Text size="sm" c="dimmed">-</Text>
            }
          />
          <StatRow label={t("delivery:route.wizardUi.stats.date")} value={plannedDate ?? "-"} />
          <StatRow
            label={t("delivery:route.wizardUi.stats.channel")}
            value={
              detectedChannel === "LOCAL_DELIVERY" ? (
                <Badge
                  size="sm"
                  color="green"
                  variant="light"
                  radius="sm"
                  leftSection={<TruckIcon size={16} weight="fill" />}
                >
                  {t("delivery:route.wizardUi.channel.LOCAL_DELIVERY")}
                </Badge>
              ) : detectedChannel === "MIXED" ? (
                <Badge
                  size="sm"
                  color="orange"
                  variant="light"
                  radius="sm"
                  leftSection={<WarningIcon size={16} weight="fill" />}
                >
                  {t("delivery:route.wizardUi.stats.mixed")}
                </Badge>
              ) : <Text size="sm" c="dimmed">-</Text>
            }
          />
        </Stack>
      </Box>

      {/* Validation list */}
      {orderCount > 0 && (
        <Box px="lg" py="sm" bg="gray.0" style={{ borderTop: "1px solid var(--mantine-color-gray-2)" }}>
          <Stack gap={0}>
            <ValRow
              status={detectedChannel === "LOCAL_DELIVERY" ? "pass" : "fail"}
              bold={detectedChannel === "LOCAL_DELIVERY" ? t("delivery:route.wizardUi.val.sameChannel") : t("delivery:route.wizardUi.val.channelMismatch")}
              detail={detectedChannel === "LOCAL_DELIVERY" ? t("delivery:route.wizardUi.channel.LOCAL_DELIVERY") : t("delivery:route.wizardUi.val.channelMismatchDetail")}
            />
            <ValRow
              status={unclassified === 0 ? "pass" : "warn"}
              bold={unclassified === 0 ? t("delivery:route.wizardUi.val.classified") : t("delivery:route.wizardUi.val.unclassified", { count: unclassified })}
              detail={unclassified === 0 ? undefined : t("delivery:route.wizardUi.val.unclassifiedDetail")}
            />
            <ValRow
              status={dates.size <= 1 ? "pass" : "warn"}
              bold={dates.size <= 1 ? t("delivery:route.wizardUi.val.sameDate") : t("delivery:route.wizardUi.val.dateMismatch")}
              detail={dates.size <= 1 ? undefined : t("delivery:route.wizardUi.val.dateMismatchDetail", { count: dates.size })}
            />
            <ValRow
              status={slaBreached === 0 ? "pass" : "warn"}
              bold={slaBreached === 0 ? t("delivery:route.wizardUi.val.noSla") : t("delivery:route.wizardUi.val.sla", { count: slaBreached })}
              detail={slaBreached === 0 ? undefined : t("delivery:route.wizardUi.val.putFirst")}
            />
            {(vipCount + urgentCount) > 0 && (
              <ValRow
                status="warn"
                bold={t("delivery:route.wizardUi.val.priority", { count: vipCount + urgentCount })}
                detail={t("delivery:route.wizardUi.val.putFirst")}
              />
            )}
          </Stack>
        </Box>
      )}
    </Box>
  )
}
